import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Code } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { Message } from '../types/chat.types';

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  if (message.type === 'user') {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end' }}
        data-testid="chat-message"
        data-role="user"
      >
        <Paper
          sx={{
            p: 1.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            maxWidth: '80%',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2">{message.content}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
            {message.timestamp.toLocaleTimeString()}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (message.type === 'error') {
    return (
      <Alert
        severity="error"
        sx={{ fontSize: '0.875rem' }}
        data-testid="chat-message"
        data-role="error"
      >
        {message.content}
      </Alert>
    );
  }

  // Assistant message. `data-testid` + `data-role` are the browser tier's
  // handle on the rendered bubble (tests/e2e-browser/tests/chat-render.spec.ts,
  // #1111) — the widget has no other stable selector.
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'flex-start' }}
      data-testid="chat-message"
      data-role="assistant"
    >
      <Paper sx={{ p: 1.5, bgcolor: 'white', maxWidth: '85%', borderRadius: 2 }}>
        <Box
          sx={{
            mb: 1,
            fontSize: '0.875rem',
            '& p': { margin: '0.5em 0' },
            '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
            '& li': { margin: '0.25em 0' },
            '& code': {
              bgcolor: 'grey.100',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.85em',
            },
            '& pre': {
              bgcolor: 'grey.100',
              p: 1,
              borderRadius: 1,
              overflowX: 'auto',
            },
            '& strong': { fontWeight: 'bold' },
            '& em': { fontStyle: 'italic' },
          }}
        >
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </Box>

        {message.queryResult && (
          <>
            {/* SQL Query Accordion */}
            {message.queryResult.sql_query && (
              <Accordion elevation={0} sx={{ mt: 1 }}>
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Code size={14} />
                    <Typography variant="caption">View SQL</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    sx={{
                      p: 1,
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      overflowX: 'auto',
                    }}
                  >
                    {message.queryResult.sql_query}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Query Breakdown Accordion */}
            {message.queryResult.phase_breakdown && (
              <Accordion elevation={0} sx={{ mt: 1 }}>
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                      Query Breakdown (
                      {(message.queryResult.phase_breakdown.total_ms / 1000).toFixed(1)}
                      s)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {message.queryResult.phase_breakdown.thinking_ms && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                        >
                          Thinking
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                        >
                          {(message.queryResult.phase_breakdown.thinking_ms / 1000).toFixed(2)}s
                        </Typography>
                      </Box>
                    )}
                    {message.queryResult.phase_breakdown.tool_execution_ms && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                        >
                          Execution
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                        >
                          {(message.queryResult.phase_breakdown.tool_execution_ms / 1000).toFixed(
                            2
                          )}
                          s
                        </Typography>
                      </Box>
                    )}
                    {message.queryResult.phase_breakdown.formatting_ms && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                        >
                          Formatting
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                        >
                          {(message.queryResult.phase_breakdown.formatting_ms / 1000).toFixed(2)}s
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 0.5,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Total
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: 'bold' }}
                      >
                        {(message.queryResult.phase_breakdown.total_ms / 1000).toFixed(2)}s
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}

        <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
          {message.timestamp.toLocaleTimeString()}
        </Typography>
      </Paper>
    </Box>
  );
};

export default MessageBubble;
